import { NxtImportItem } from '../NxtImportContext';
import { Box, LinearProgress, Typography, Button } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { importSingleSx, importDataSx, importProgressSx } from './ImportFieldInputStyles';
import { IconHelper } from 'components/generic/IconHelper';
import { truncateString } from 'utility/functions';
import { AssetType } from 'utility/types/cosmosTypes';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { FullEditorialAsset } from 'utility/types/dataProvider';
import { StatusChip } from 'components/Search/components/fields/generic/StatusChip';

type FWSItemStatusProps = {
  onCancelClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isFile?: boolean; // file or wire import
  progress?: number;
  errorMessage?: string | undefined;
  assetStatus?: string | undefined | null;
};

export const FWSItemStatus = ({
  onCancelClick,
  isFile,
  assetStatus,
  progress,
  errorMessage,
}: FWSItemStatusProps) => {
  const { t } = useTranslation();
  const strCancel = t('blox.actions.cancel');
  const strProcessing = t('blox.general.processing');
  const strImporting = t('blox.general.importing');
  const strCanceledByUser = t('blox.general.canceled_by_user');
  const strQueued = t('blox.general.queued');

  switch (assetStatus) {
    case 'success':
    case 'imported': {
      return (
        <Typography
          sx={(theme) => ({
            color: theme.palette.success.main,
            textTransform: 'capitalize',
          })}
        >
          {assetStatus}
        </Typography>
      );
    }
    case 'uploading': {
      return (
        <Fragment>
          {isFile && (
            <Button
              data-testid="batch-item-cancel-upload-button"
              onClick={onCancelClick}
              startIcon={<CancelIcon />}
              color="warning"
              size="small"
            >
              {strCancel}
            </Button>
          )}
          <Typography sx={(theme) => ({ color: theme.palette.text.secondary })}>
            {`${progress}%`}
          </Typography>
        </Fragment>
      );
    }
    case 'processing': {
      return (
        <Typography sx={(theme) => ({ color: theme.palette.text.secondary })}>
          {strProcessing}
        </Typography>
      );
    }
    case 'importing': {
      return (
        <Typography sx={(theme) => ({ color: theme.palette.text.secondary })}>
          {strImporting}
        </Typography>
      );
    }
    case 'queued': {
      return (
        <Fragment>
          {isFile && (
            <Button
              data-testid="batch-item-cancel-queued-button"
              onClick={onCancelClick}
              startIcon={<CancelIcon />}
              color="warning"
              size="small"
            >
              {strCancel}
            </Button>
          )}
          <Typography sx={(theme) => ({ color: theme.palette.text.secondary })}>
            {strQueued}
          </Typography>
        </Fragment>
      );
    }
    case 'canceled': {
      return (
        <Typography sx={(theme) => ({ color: theme.palette.warning.main })}>
          {strCanceledByUser}
        </Typography>
      );
    }
    case 'error':
    case 'import-failed':
    case null: {
      return (
        <Typography sx={(theme) => ({ color: theme.palette.error.main })}>
          {errorMessage}
        </Typography>
      );
    }
    default: {
      return null;
    }
  }
};

type BatchFieldSingleProps = {
  item: NxtImportItem;
  onCancelClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  edit?: boolean;
  isNested?: boolean;
};

const ImportFieldSingleUpload = ({
  item,
  edit,
  onCancelClick,
  isNested = false,
}: BatchFieldSingleProps) => {
  const { t } = useTranslation();

  if (item.inlineAsset) {
    // If the item is an inline asset, we don't render anything for this component.
    return null;
  }

  // Determine the status string for display based on the boolean flags
  let assetStatus: string | undefined | null;
  if (item.isQueued) {
    assetStatus = 'queued';
  } else if (item.isUploading) {
    assetStatus = 'uploading';
  } else if (item.isProcessing) {
    assetStatus = 'processing';
  } else if (item.isReady) {
    assetStatus = 'success';
  } else if (item.isFailed) {
    assetStatus = 'error';
  } else if (item.isCanceled) {
    assetStatus = 'canceled';
  }

  const progress = item.progress;
  const isFile = item?.file !== undefined;
  const isImportFile = item.importFile;
  const isSyndication = item.resourceType === 'wire';

  // Determine the name and type to display
  let name: string | undefined;
  let type: AssetType | string | undefined = item.type;
  let errorMessage: string | undefined;
  let assetData: Partial<FullEditorialAsset> | null = null;

  if ((isFile || isImportFile) && item.fileName) {
    name = truncateString(item.fileName, 45);
    assetData = item?.data ?? null; // Pass the full asset data
  } else if (item.file) {
    name = truncateString(item.file.name, 45);
    assetData = item?.data ?? null; // Pass the full asset data
  } else if (isSyndication) {
    // For syndication imports, we might need different naming logic
    name = truncateString(item.fileName || 'Syndication Import', 45);
    assetData = null;
  } else {
    name = 'Unknown Import';
    assetData = null;
  }

  // Set error message for failed imports
  if (item.isFailed) {
    switch (item.resourceType) {
      case 'wire':
        errorMessage = 'Import Failed';
        break;
      default:
        errorMessage = 'Upload Failed';
        break;
    }
  }

  const strEditUnsupported = t('blox.general.edit_not_supported', {
    type: type || '',
  });

  const strAssetsImported = t('blox.import.assets.imported');
  const strBpmImportWarning = t('blox.batch_import.notifications.bpm.warn');

  return (
    <Box sx={(theme) => importSingleSx(theme, isNested)}>
      <Box
        data-testid="batch-upload-item-wrapper"
        sx={{
          ...importDataSx,
          display: 'grid',
          gridTemplateColumns: '2fr auto 1fr',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* Left column - File name and icon */}
        <Box
          data-testid="batch-upload-item-file-name-wrapper"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
        >
          {type && <IconHelper iconType={type} />}
          <Typography
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            color={
              assetStatus === 'error' ? 'error' : edit ? 'primary' : 'text.secondary'
            }
          >
            {name || 'Unknown'}
          </Typography>
          {item.isReady && item.data?.url && <OpenInNewIcon color="primary" />}
        </Box>

        {/* Center column - Primary asset status */}
        <Box
          data-testid="batch-upload-item-publish-status-wrapper"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 120,
          }}
        >
          {assetStatus === 'success' && assetData && (
            <StatusChip
              data-testid={`batch-upload-item-publish-status--${assetData?.uuid ?? assetData?.id}`}
              record={assetData}
              isOverlay={false}
              iconSize={14}
              tint={0.12}
              chipVariant="outlined"
              ignoreScheduled
            />
          )}
        </Box>

        {/* Right column - Upload status */}
        <Box
          data-testid="batch-upload-item-upload-status-wrapper"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            minWidth: 'fit-content',
          }}
        >
          <FWSItemStatus
            onCancelClick={onCancelClick}
            isFile={isFile}
            assetStatus={assetStatus}
            progress={progress}
            errorMessage={errorMessage}
          />
        </Box>
      </Box>

      {(assetStatus === 'uploading' ||
        assetStatus === 'processing' ||
        assetStatus === 'importing') && (
        <Box
          data-testid="batch-upload-item-progress-bar-wrapper"
          sx={{ width: '100%' }}
        >
          <LinearProgress
            variant={assetStatus === 'uploading' ? 'determinate' : 'indeterminate'}
            value={progress}
            sx={importProgressSx}
          />
        </Box>
      )}
      {!edit && !isImportFile && assetStatus === 'success' && (
        <Box
          data-testid="batch-upload-item-edit-unsupported-wrapper"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Typography
            sx={(theme) => ({
              color: theme.palette.warning.main,
              fontSize: '0.75rem !important',
            })}
          >
            {strEditUnsupported}
          </Typography>
        </Box>
      )}

      {item.resourceType === 'wire' && item.misc?.channel_name && (
        <Box
          data-testid="batch-upload-item-channel-name-wrapper"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Typography
            sx={(theme) => ({
              color: theme.palette.info.main,
              fontSize: '0.75rem !important',
            })}
          >
            From {item.misc?.channel_name}
          </Typography>
        </Box>
      )}

      {item.isFailed && item.misc?.errorMessage && (
        <Box
          data-testid="batch-upload-item-error-message-wrapper"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Typography
            sx={(theme) => ({
              color: theme.palette.warning.main,
              fontSize: '0.75rem !important',
            })}
          >
            {item.misc?.errorMessage}
          </Typography>
        </Box>
      )}

      {isImportFile && (
        <Box
          data-testid="batch-upload-item-assets-imported-wrapper"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={(theme) => ({
              color: theme.palette.warning.main,
              fontSize: '0.75rem !important',
            })}
          >
            {strBpmImportWarning}
          </Typography>
          {assetStatus === 'success' && (
            <Typography
              sx={(theme) => ({
                color: theme.palette.warning.main,
                fontSize: '0.75rem !important',
              })}
            >
              {strAssetsImported}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

ImportFieldSingleUpload.displayName = 'ImportFieldSingleUpload';
export default ImportFieldSingleUpload;
